import { useMutation, useLazyQuery } from '@apollo/client'
import { useTheme } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import apolloClient from '../../graphql/apolloClient'
import { ROOMTYPE_QUERY } from '../../graphql/roomtype'
import { ROOMTYPE_GENERATE_MUTATION } from '../../graphql/roomtypeGenerate'
import { ROOMTYPE_MODIFY_MUTATION } from '../../graphql/roomtypeModify'
import { lang } from '../../localization'
import { RoomType } from '../../types/roomtype'
import { RoomtypeBasicDetails } from './RoomtypeBasicDetails'
import { RoomtypeFooter } from './RoomtypeFooter'
import { RoomtypeInventoryAndBookingDetails } from './RoomtypeInventoryAndBookingSDetails'
import { RoomtypeVirtualRoomDetails } from './RoomtypeVirtualRoomDetails'
import useStyles from './styles/RoomtypeMainForm.styles'
import { roomtypeFormValidationSchema } from './validations/roomtypeFormValidation'

const ROOMTYPE_FORM_INIT_VALUES: RoomType = {
  basicDetails: {
    name: '',
    nameTranslatedList: [
      { label: 'Iran, Islamic Republic of', value: '', nativeLabel: 'فارسی' }
    ],
    roomTypeCode: '',
    code: '',
    defaultRackRate: 0,
    description: '',
    descriptionTranslatedList: [
      { label: 'Iran, Islamic Republic of', value: '', nativeLabel: 'فارسی' }
    ],
    roomTypeID: '',
    type: 'Physical',
    active: true,
    deleted: true
  },
  virtualRoomDetails: {
    parentRoomTypeCode: '',
    virtualRoomAvailabilityMultiplier: 0
  },
  inventoryAndBooking: {
    defaultOccupancy: 0,
    maxOccupancy: 0,
    numberOfRooms: 0
  }
}

const RoomtypeMainForm = () => {
  const classes = useStyles()
  const theme = useTheme()
  const params = useParams<{
    accommodationID: string
    propertyCode: string
    roomTypeId: string
    roomTypeCode: string
  }>()

  const history = useHistory()
  const [initialValues, setinitialValues] = useState<RoomType>(
    ROOMTYPE_FORM_INIT_VALUES
  )

  const [roomTypeOnSubmitFunction, { loading: loadingForRoomType }] =
    useMutation(
      params.roomTypeCode
        ? ROOMTYPE_MODIFY_MUTATION
        : ROOMTYPE_GENERATE_MUTATION
    )

  const [getRoomType, { loading }] = useLazyQuery<{
    roomType: RoomType
  }>(ROOMTYPE_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setinitialValues({
        ...data.roomType,
        basicDetails: {
          ...data.roomType.basicDetails,
          code: data.roomType.basicDetails.displayRoomTypeCode || ''
        }
      })
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const handleSubmit = async (values: RoomType) => {
    const {
      roomTypeID,
      active,
      deleted,
      displayRoomTypeCode,
      roomTypeCode,
      ...newBasicDetails
    } = values.basicDetails
    const newValues = { ...values, basicDetails: newBasicDetails }

    await roomTypeOnSubmitFunction(
      params.roomTypeCode
        ? {
            variables: {
              propertyCode: params.propertyCode,
              roomTypeCode: params.roomTypeCode,
              input: newValues
            }
          }
        : {
            variables: {
              propertyCode: params.propertyCode,
              input: newValues
            }
          }
    )
      .then(({ data: response }) => {
        apolloClient.cache.evict({
          fieldName: 'roomTypes'
        })
        if (params.roomTypeCode) {
          toast.success(response.modifyRoomType.message)
          history.push(
            `/${lang}/accommodations/${params.accommodationID}/${params.propertyCode}/room-types/list`
          )
        } else {
          toast.success(response.generateRoomType.message)
          history.push(
            `/${lang}/accommodations/${params.accommodationID}/${params.propertyCode}/room-types/list`
          )
        }
      })
      .catch((err) => toast.error(err.message))
  }

  useEffect(() => {
    if (!!params.roomTypeCode) {
      getRoomType({
        variables: {
          propertyCode: params.propertyCode,
          roomTypeCode: params.roomTypeCode
        }
      })
    }
  }, [])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={roomtypeFormValidationSchema}>
      {({
        values,
        setFieldValue,
        errors,
        touched,
        handleBlur,
        isSubmitting
      }) => {
        const isPhysical = values.basicDetails.type === 'Physical'
        return (
          <>
            <Form>
              <RoomtypeBasicDetails
                {...{
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  handleBlur,
                  isSubmitting,
                  isPhysical,
                  propertyCode: params.propertyCode,
                  classes
                }}
              />
              <RoomtypeInventoryAndBookingDetails
                {...{
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  handleBlur,
                  isSubmitting,
                  classes
                }}
              />
              {!isPhysical && (
                <RoomtypeVirtualRoomDetails
                  {...{
                    values,
                    setFieldValue,
                    errors,
                    touched,
                    handleBlur,
                    isSubmitting,
                    classes
                  }}
                />
              )}
              <RoomtypeFooter
                isSubmitting={isSubmitting}
                loadingForRoomType={loadingForRoomType}
              />
            </Form>
            <AdotelSkeleton
              open={loading}
              circlesColor={theme.palette.primary.main}
              numberOfCircles={4}
            />
          </>
        )
      }}
    </Formik>
  )
}

export default RoomtypeMainForm
